/**
 * Geometry-aware capture-step planning for muscleBalance.
 *
 * Pure functions only — no box3d / React / Three. LIP capture + 2-link IK.
 * Checkpoint 18: horizontal support hull + outward expansion targets so steps
 * enlarge the base instead of planting inside it.
 */

export type CaptureSwingSide = 'left' | 'right';

export interface CaptureVec3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface LipCapturePoint {
  readonly x: number;
  readonly z: number;
  readonly omega0: number;
  readonly heightM: number;
}

/** Axis-aligned horizontal support hull (XZ) from both feet. */
export interface SupportHull2D {
  readonly minX: number;
  readonly maxX: number;
  readonly minZ: number;
  readonly maxZ: number;
  readonly centerX: number;
  readonly centerZ: number;
  /** Max of (width, depth) — used as a span proxy. */
  readonly spanM: number;
}

export interface CaptureStepPlan {
  readonly swingSide: CaptureSwingSide;
  readonly targetWorldX: number;
  readonly targetWorldY: number;
  readonly targetWorldZ: number;
  readonly targetRelXM: number;
  readonly targetRelZM: number;
  readonly supportSpanBeforeM: number;
  readonly supportSpanAfterM: number;
  readonly lip: LipCapturePoint;
  /** Unit fall/capture direction (XZ). */
  readonly fallDirX: number;
  readonly fallDirZ: number;
  readonly hullBefore: SupportHull2D;
  readonly hullAfter: SupportHull2D;
}

export interface TwoLinkIkResult {
  readonly hipSagittalRad: number;
  readonly hipLateralRad: number;
  readonly kneeRad: number;
  readonly ankleRad: number;
  readonly reachM: number;
  readonly reachable: boolean;
}

export const CAPTURE_THIGH_LENGTH_M = 0.16 * 2 + 0.065 * 2;
export const CAPTURE_SHANK_LENGTH_M = 0.17 * 2 + 0.05 * 2;
export const CAPTURE_FOOT_DROP_M = 0.06;
export const CAPTURE_GRAVITY_MPS2 = 9.81;
export const CAPTURE_MIN_COM_HEIGHT_M = 0.35;
export const CAPTURE_GROUND_Y_M = 0.03;
export const CAPTURE_FOOT_NEAR_GROUND_Y_M = 0.08;

/** Anatomical half-extents for hull corners (m): half-width X, half-length Z. */
export const CAPTURE_FOOT_HALF_WIDTH_M = 0.05;
export const CAPTURE_FOOT_HALF_LENGTH_M = 0.13;
/** Documented outward expansion beyond current hull (m). */
export const CAPTURE_HULL_EXPAND_MARGIN_M = 0.05;

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function hypot2(x: number, z: number): number {
  return Math.hypot(x, z);
}

function normalize2(x: number, z: number): { x: number; z: number } {
  const n = Math.hypot(x, z);
  if (n < 1e-9) return { x: 0, z: 1 };
  return { x: x / n, z: z / n };
}

export function computeLipCapturePoint(
  comX: number,
  comZ: number,
  velX: number,
  velZ: number,
  comHeightM: number,
  gravity = CAPTURE_GRAVITY_MPS2,
): LipCapturePoint {
  const heightM = Math.max(comHeightM, CAPTURE_MIN_COM_HEIGHT_M);
  const omega0 = Math.sqrt(Math.max(gravity, 1e-6) / heightM);
  return {
    x: comX + velX / omega0,
    z: comZ + velZ / omega0,
    omega0,
    heightM,
  };
}

/**
 * Horizontal support hull from two foot centers + half-width/half-length.
 * Uses axis-aligned boxes around each foot; hull = AABB of both.
 */
export function buildSupportHull2D(
  leftFoot: CaptureVec3,
  rightFoot: CaptureVec3,
  halfWidthM = CAPTURE_FOOT_HALF_WIDTH_M,
  halfLengthM = CAPTURE_FOOT_HALF_LENGTH_M,
): SupportHull2D {
  const minX = Math.min(leftFoot.x, rightFoot.x) - halfWidthM;
  const maxX = Math.max(leftFoot.x, rightFoot.x) + halfWidthM;
  const minZ = Math.min(leftFoot.z, rightFoot.z) - halfLengthM;
  const maxZ = Math.max(leftFoot.z, rightFoot.z) + halfLengthM;
  const width = maxX - minX;
  const depth = maxZ - minZ;
  return {
    minX,
    maxX,
    minZ,
    maxZ,
    centerX: (minX + maxX) * 0.5,
    centerZ: (minZ + maxZ) * 0.5,
    spanM: Math.max(width, depth),
  };
}

/** Project a ray from hull center in unit direction (dx,dz) to the hull edge. */
export function projectRayToHullBoundary(
  hull: SupportHull2D,
  dirX: number,
  dirZ: number,
): { x: number; z: number; distM: number } {
  const d = normalize2(dirX, dirZ);
  // AABB ray from center: t = min positive hit on each slab.
  let tHit = Number.POSITIVE_INFINITY;
  if (Math.abs(d.x) > 1e-9) {
    const tx = d.x > 0 ? (hull.maxX - hull.centerX) / d.x : (hull.minX - hull.centerX) / d.x;
    if (tx > 0) tHit = Math.min(tHit, tx);
  }
  if (Math.abs(d.z) > 1e-9) {
    const tz = d.z > 0 ? (hull.maxZ - hull.centerZ) / d.z : (hull.minZ - hull.centerZ) / d.z;
    if (tz > 0) tHit = Math.min(tHit, tz);
  }
  if (!Number.isFinite(tHit) || tHit <= 0) {
    // Degenerate direction: use half-diagonal.
    tHit = 0.5 * Math.hypot(hull.maxX - hull.minX, hull.maxZ - hull.minZ);
  }
  return {
    x: hull.centerX + d.x * tHit,
    z: hull.centerZ + d.z * tHit,
    distM: tHit,
  };
}

/**
 * Fall/capture direction: DCM (LIP) − support center, velocity as tie-break.
 * Pure vector geometry — no L/R/F/B special cases.
 */
export function captureFallDirection(args: {
  lipX: number;
  lipZ: number;
  centerX: number;
  centerZ: number;
  velX: number;
  velZ: number;
}): { x: number; z: number } {
  let dx = args.lipX - args.centerX;
  let dz = args.lipZ - args.centerZ;
  if (Math.hypot(dx, dz) < 0.02) {
    dx = args.velX;
    dz = args.velZ;
  }
  if (Math.hypot(dx, dz) < 1e-6) {
    return { x: 0, z: 1 }; // neutral forward if fully quiescent
  }
  return normalize2(dx, dz);
}

/**
 * Swing foot whose outward move expands the hull in fallDir.
 * Prefer the foot with larger projection onto fallDir (outer foot).
 */
export function selectSwingForHullExpansion(
  leftFoot: CaptureVec3,
  rightFoot: CaptureVec3,
  fallDirX: number,
  fallDirZ: number,
): CaptureSwingSide {
  const midX = (leftFoot.x + rightFoot.x) * 0.5;
  const midZ = (leftFoot.z + rightFoot.z) * 0.5;
  const leftProj =
    (leftFoot.x - midX) * fallDirX + (leftFoot.z - midZ) * fallDirZ;
  const rightProj =
    (rightFoot.x - midX) * fallDirX + (rightFoot.z - midZ) * fallDirZ;
  if (Math.abs(rightProj - leftProj) > 1e-4) {
    return rightProj >= leftProj ? 'right' : 'left';
  }
  // Tie: foot on the side of fallDir relative mid.
  return fallDirX * (rightFoot.x - leftFoot.x) + fallDirZ * (rightFoot.z - leftFoot.z) >= 0
    ? 'right'
    : 'left';
}

/** @deprecated Prefer selectSwingForHullExpansion; kept for callers/tests. */
export function selectCaptureSwingSide(
  leftFoot: CaptureVec3,
  rightFoot: CaptureVec3,
  lip: LipCapturePoint,
  comX: number,
  velX: number,
): CaptureSwingSide {
  const midX = (leftFoot.x + rightFoot.x) * 0.5;
  const midZ = (leftFoot.z + rightFoot.z) * 0.5;
  const fall = captureFallDirection({
    lipX: lip.x,
    lipZ: lip.z,
    centerX: midX,
    centerZ: midZ,
    velX,
    velZ: 0,
  });
  void comX;
  return selectSwingForHullExpansion(leftFoot, rightFoot, fall.x, fall.z);
}

/**
 * Clamp world capture target to a reachable horizontal disk and max step box
 * relative to the swing foot's current position.
 */
export function clampCaptureFootTarget(
  swingFoot: CaptureVec3,
  desiredX: number,
  desiredZ: number,
  maxLengthM: number,
  maxWidthM: number,
  groundYM = CAPTURE_GROUND_Y_M,
): { x: number; y: number; z: number } {
  let dx = desiredX - swingFoot.x;
  let dz = desiredZ - swingFoot.z;
  dx = clamp(dx, -maxWidthM, maxWidthM);
  dz = clamp(dz, -maxLengthM, maxLengthM);
  const span = Math.hypot(dx, dz);
  const maxSpan = Math.hypot(maxWidthM, maxLengthM);
  if (span > maxSpan && span > 1e-9) {
    const s = maxSpan / span;
    dx *= s;
    dz *= s;
  }
  return {
    x: swingFoot.x + dx,
    y: groundYM,
    z: swingFoot.z + dz,
  };
}

/**
 * Hull-expanding capture plan (checkpoint 18).
 *
 * 1. Build support hull from both feet.
 * 2. Fall dir = normalize(LIP − hull center), velocity tie-break.
 * 3. Desired land = hull boundary + expandMargin along fall dir (optional LIP blend).
 * 4. Swing = outer foot for hull expansion in fall dir.
 * 5. Clamp to swing-foot reachable workspace.
 * 6. If clamp would shrink hull span, push back out along fall dir within reach.
 */
export function planGeometryCaptureStep(args: {
  com: CaptureVec3;
  comVelocity: CaptureVec3;
  supportCenter: CaptureVec3;
  leftFoot: CaptureVec3;
  rightFoot: CaptureVec3;
  maxLengthM: number;
  maxWidthM: number;
  groundYM?: number;
  gravity?: number;
  lipGain?: number;
  expandMarginM?: number;
  footHalfWidthM?: number;
  footHalfLengthM?: number;
}): CaptureStepPlan {
  const {
    com,
    comVelocity,
    supportCenter,
    leftFoot,
    rightFoot,
    maxLengthM,
    maxWidthM,
    groundYM = CAPTURE_GROUND_Y_M,
    gravity = CAPTURE_GRAVITY_MPS2,
    lipGain = 0.42,
    expandMarginM = CAPTURE_HULL_EXPAND_MARGIN_M,
    footHalfWidthM = CAPTURE_FOOT_HALF_WIDTH_M,
    footHalfLengthM = CAPTURE_FOOT_HALF_LENGTH_M,
  } = args;

  const hullBefore = buildSupportHull2D(leftFoot, rightFoot, footHalfWidthM, footHalfLengthM);
  const comHeight = Math.max(com.y - groundYM, CAPTURE_MIN_COM_HEIGHT_M);
  const lip = computeLipCapturePoint(
    com.x,
    com.z,
    comVelocity.x,
    comVelocity.z,
    comHeight,
    gravity,
  );

  const fall = captureFallDirection({
    lipX: lip.x,
    lipZ: lip.z,
    centerX: hullBefore.centerX,
    centerZ: hullBefore.centerZ,
    velX: comVelocity.x,
    velZ: comVelocity.z,
  });

  const swingSide = selectSwingForHullExpansion(leftFoot, rightFoot, fall.x, fall.z);
  const swingFoot = swingSide === 'left' ? leftFoot : rightFoot;
  const stanceFoot = swingSide === 'left' ? rightFoot : leftFoot;

  // Boundary of current hull in fall direction + expansion margin.
  const edge = projectRayToHullBoundary(hullBefore, fall.x, fall.z);
  const beyondX = edge.x + fall.x * expandMarginM;
  const beyondZ = edge.z + fall.z * expandMarginM;

  // Optional blend toward LIP (keeps DCM influence without landing inside hull).
  const g = clamp(lipGain, 0, 1);
  const lipBlendX = beyondX * (1 - g * 0.35) + lip.x * (g * 0.35);
  const lipBlendZ = beyondZ * (1 - g * 0.35) + lip.z * (g * 0.35);
  // Prefer beyond-hull point; if LIP is further out in fall dir, allow it.
  const beyondDot = (beyondX - hullBefore.centerX) * fall.x + (beyondZ - hullBefore.centerZ) * fall.z;
  const lipDot = (lipBlendX - hullBefore.centerX) * fall.x + (lipBlendZ - hullBefore.centerZ) * fall.z;
  let desiredX = lipDot > beyondDot ? lipBlendX : beyondX;
  let desiredZ = lipDot > beyondDot ? lipBlendZ : beyondZ;

  let clamped = clampCaptureFootTarget(
    swingFoot,
    desiredX,
    desiredZ,
    maxLengthM,
    maxWidthM,
    groundYM,
  );

  // Ensure target is still outside hull along fall dir after reach clamp.
  const ensureOutside = (pt: { x: number; y: number; z: number }) => {
    const edge2 = projectRayToHullBoundary(hullBefore, fall.x, fall.z);
    const minOut = edge2.distM + expandMarginM * 0.75;
    const fromC = {
      x: pt.x - hullBefore.centerX,
      z: pt.z - hullBefore.centerZ,
    };
    const proj = fromC.x * fall.x + fromC.z * fall.z;
    if (proj >= minOut) return pt;
    // Push along fall dir to minOut, then re-clamp to swing reach.
    const pushed = {
      x: hullBefore.centerX + fall.x * minOut,
      z: hullBefore.centerZ + fall.z * minOut,
    };
    return clampCaptureFootTarget(
      swingFoot,
      pushed.x,
      pushed.z,
      maxLengthM,
      maxWidthM,
      groundYM,
    );
  };
  clamped = ensureOutside(clamped);

  const hullAfter = buildSupportHull2D(
    swingSide === 'left' ? clamped : leftFoot,
    swingSide === 'right' ? clamped : rightFoot,
    footHalfWidthM,
    footHalfLengthM,
  );

  const supportSpanBeforeM = supportSpanM(leftFoot, rightFoot);
  const supportSpanAfterM = supportSpanM(
    swingSide === 'left' ? clamped : leftFoot,
    swingSide === 'right' ? clamped : rightFoot,
  );

  // If reach clamp still failed to expand hull along fall dir, step the swing
  // foot along fall dir by the maximum reachable amount (mirrored geometry).
  let hullAfterTry = hullAfter;
  let spanAfterTry = supportSpanAfterM;
  const extentBefore = hullExtentAlongDir(hullBefore, fall.x, fall.z);
  const extentAfter = hullExtentAlongDir(hullAfterTry, fall.x, fall.z);
  if (
    extentAfter + 1e-4 < extentBefore + expandMarginM * 0.5 ||
    spanAfterTry + 1e-4 < supportSpanBeforeM + expandMarginM * 0.25
  ) {
    const maxReach = Math.hypot(maxWidthM, maxLengthM);
    const reachTarget = {
      x: swingFoot.x + fall.x * maxReach,
      z: swingFoot.z + fall.z * maxReach,
    };
    clamped = clampCaptureFootTarget(
      swingFoot,
      reachTarget.x,
      reachTarget.z,
      maxLengthM,
      maxWidthM,
      groundYM,
    );
  }

  const spanAfterFinal = supportSpanM(
    swingSide === 'left' ? clamped : leftFoot,
    swingSide === 'right' ? clamped : rightFoot,
  );
  const hullAfterFinal = buildSupportHull2D(
    swingSide === 'left' ? clamped : leftFoot,
    swingSide === 'right' ? clamped : rightFoot,
    footHalfWidthM,
    footHalfLengthM,
  );

  void stanceFoot;
  void supportCenter;

  return {
    swingSide,
    targetWorldX: clamped.x,
    targetWorldY: clamped.y,
    targetWorldZ: clamped.z,
    targetRelXM: clamped.x - hullBefore.centerX,
    targetRelZM: clamped.z - hullBefore.centerZ,
    supportSpanBeforeM,
    supportSpanAfterM: spanAfterFinal,
    lip,
    fallDirX: fall.x,
    fallDirZ: fall.z,
    hullBefore,
    hullAfter: hullAfterFinal,
  };
}

/**
 * Explicit commanded one-step plan (Gate A).
 *
 * Places the swing foot a prescribed forward/lateral displacement from its
 * current plant pose. Independent of LIP/fall-direction capture heuristics.
 * Default swing side is deterministic (`right`) so same-input replays match.
 */
export function planExplicitForwardStep(args: {
  leftFoot: CaptureVec3;
  rightFoot: CaptureVec3;
  supportCenter: CaptureVec3;
  /** Commanded forward displacement of the swing foot (m). Gate A: 0.22. */
  forwardDisplacementM: number;
  /** Commanded lateral displacement of the swing foot (m, +right). */
  lateralDisplacementM?: number;
  /** When omitted, defaults to `right` (deterministic). */
  swingSide?: CaptureSwingSide;
  maxLengthM: number;
  maxWidthM: number;
  groundYM?: number;
  footHalfWidthM?: number;
  footHalfLengthM?: number;
}): CaptureStepPlan & {
  unclampedTargetX: number;
  unclampedTargetZ: number;
  targetClamped: boolean;
} {
  const {
    leftFoot,
    rightFoot,
    supportCenter,
    forwardDisplacementM,
    lateralDisplacementM = 0,
    swingSide = 'right',
    maxLengthM,
    maxWidthM,
    groundYM = CAPTURE_GROUND_Y_M,
    footHalfWidthM = CAPTURE_FOOT_HALF_WIDTH_M,
    footHalfLengthM = CAPTURE_FOOT_HALF_LENGTH_M,
  } = args;

  const swingFoot = swingSide === 'left' ? leftFoot : rightFoot;
  const unclampedX = swingFoot.x + lateralDisplacementM;
  const unclampedZ = swingFoot.z + forwardDisplacementM;
  const clamped = clampCaptureFootTarget(
    swingFoot,
    unclampedX,
    unclampedZ,
    maxLengthM,
    maxWidthM,
    groundYM,
  );
  const targetClamped =
    Math.hypot(clamped.x - unclampedX, clamped.z - unclampedZ) > 1e-4;

  const hullBefore = buildSupportHull2D(leftFoot, rightFoot, footHalfWidthM, footHalfLengthM);
  const hullAfter = buildSupportHull2D(
    swingSide === 'left' ? clamped : leftFoot,
    swingSide === 'right' ? clamped : rightFoot,
    footHalfWidthM,
    footHalfLengthM,
  );

  const comHeight = Math.max(supportCenter.y + 1.0 - groundYM, CAPTURE_MIN_COM_HEIGHT_M);
  const lip = computeLipCapturePoint(
    supportCenter.x,
    supportCenter.z,
    0,
    forwardDisplacementM > 0 ? 0.1 : 0,
    comHeight,
  );

  const fallDirZ = Math.abs(forwardDisplacementM) > 1e-9 ? Math.sign(forwardDisplacementM) : 1;
  const fallDirX =
    Math.abs(lateralDisplacementM) > 1e-9 ? Math.sign(lateralDisplacementM) : 0;
  const fall = normalize2(fallDirX, fallDirZ);

  return {
    swingSide,
    targetWorldX: clamped.x,
    targetWorldY: clamped.y,
    targetWorldZ: clamped.z,
    targetRelXM: clamped.x - hullBefore.centerX,
    targetRelZM: clamped.z - hullBefore.centerZ,
    supportSpanBeforeM: supportSpanM(leftFoot, rightFoot),
    supportSpanAfterM: supportSpanM(
      swingSide === 'left' ? clamped : leftFoot,
      swingSide === 'right' ? clamped : rightFoot,
    ),
    lip,
    fallDirX: fall.x,
    fallDirZ: fall.z,
    hullBefore,
    hullAfter,
    unclampedTargetX: unclampedX,
    unclampedTargetZ: unclampedZ,
    targetClamped,
  };
}

export function solveTwoLinkLegIk(args: {
  hip: CaptureVec3;
  footTarget: CaptureVec3;
  swingSide: CaptureSwingSide;
  thighLenM?: number;
  shankLenM?: number;
}): TwoLinkIkResult {
  const L1 = args.thighLenM ?? CAPTURE_THIGH_LENGTH_M;
  const L2 = (args.shankLenM ?? CAPTURE_SHANK_LENGTH_M) + CAPTURE_FOOT_DROP_M;
  const maxReach = L1 + L2 - 0.01;
  const minReach = Math.abs(L1 - L2) + 0.02;

  const hipJointY = args.hip.y + L1 * 0.5;
  const dx = args.footTarget.x - args.hip.x;
  const dy = args.footTarget.y - hipJointY;
  const dz = args.footTarget.z - args.hip.z;

  const planar = Math.hypot(dx, dz);
  let reach = Math.hypot(planar, dy);
  let reachable = true;
  let scale = 1;
  if (reach > maxReach) {
    reachable = false;
    scale = maxReach / Math.max(reach, 1e-9);
    reach = maxReach;
  } else if (reach < minReach) {
    reachable = false;
    scale = minReach / Math.max(reach, 1e-9);
    reach = minReach;
  }
  const usePlanar = planar * scale;
  const useDy = dy * scale;

  const cosKnee = clamp((L1 * L1 + L2 * L2 - reach * reach) / (2 * L1 * L2), -1, 1);
  const kneeInterior = Math.acos(cosKnee);
  const kneeFlex = clamp(Math.PI - kneeInterior, 0, 1.45);
  const kneeRad = -kneeFlex;

  const elev = Math.atan2(usePlanar, Math.max(-useDy, 1e-6));
  const cosGamma = clamp((L1 * L1 + reach * reach - L2 * L2) / (2 * L1 * reach), -1, 1);
  const gamma = Math.acos(cosGamma);
  const alpha = clamp(elev - gamma, -0.15, 1.2);
  const forwardSign = dz >= -0.02 ? 1 : -1;
  const hipSagittalRad = clamp(-forwardSign * alpha, -1.0, 0.35);

  const yaw = Math.atan2(dx, Math.max(Math.abs(dz), 0.05));
  let hipLateralRad = clamp(yaw, -0.55, 0.55);
  if (Math.abs(dx) < 0.03 && Math.abs(dz) > 0.05) {
    const out = args.swingSide === 'right' ? 0.06 : -0.06;
    hipLateralRad = clamp(hipLateralRad + out, -0.55, 0.55);
  }

  const ankleRad = args.footTarget.y < 0.1 ? -0.05 : 0.08;

  return {
    hipSagittalRad,
    hipLateralRad,
    kneeRad,
    ankleRad,
    reachM: reach,
    reachable,
  };
}

export function mirrorCaptureRelX(relXM: number): number {
  return -relXM;
}

export function captureLandingErrorM(
  swingFoot: CaptureVec3,
  targetWorldX: number,
  targetWorldZ: number,
): number {
  return hypot2(swingFoot.x - targetWorldX, swingFoot.z - targetWorldZ);
}

export function supportSpanM(leftFoot: CaptureVec3, rightFoot: CaptureVec3): number {
  return hypot2(rightFoot.x - leftFoot.x, rightFoot.z - leftFoot.z);
}

export function isFootNearGround(footYM: number, thresholdM = CAPTURE_FOOT_NEAR_GROUND_Y_M): boolean {
  return Number.isFinite(footYM) && footYM <= thresholdM;
}

/** True if point is outside/on the hull along fall dir by at least margin. */
export function pointExpandsHull(
  hull: SupportHull2D,
  pointX: number,
  pointZ: number,
  fallDirX: number,
  fallDirZ: number,
  marginM = 0,
): boolean {
  const edge = projectRayToHullBoundary(hull, fallDirX, fallDirZ);
  const proj =
    (pointX - hull.centerX) * fallDirX + (pointZ - hull.centerZ) * fallDirZ;
  return proj + 1e-6 >= edge.distM + marginM;
}

/**
 * Full support width of an AABB hull projected onto a unit horizontal direction.
 * Used to verify capture steps enlarge the base along the fall/capture axis.
 */
export function hullExtentAlongDir(
  hull: SupportHull2D,
  dirX: number,
  dirZ: number,
): number {
  const d = normalize2(dirX, dirZ);
  const halfW = (hull.maxX - hull.minX) * 0.5;
  const halfL = (hull.maxZ - hull.minZ) * 0.5;
  return 2 * (Math.abs(d.x) * halfW + Math.abs(d.z) * halfL);
}
